import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ClientProvider } from "@/context/ClientContext";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ImportWizard from "@/pages/ImportWizard";
import Redaktionsplan from "@/pages/Redaktionsplan";
import MediaLibrary from "@/pages/MediaLibrary";
import Analyse from "@/pages/Analyse";
import Performance from "@/pages/Performance";
import Kommentare from "@/pages/Kommentare";
import Accounts from "@/pages/Accounts";
import Fristen from "@/pages/Fristen";
import Mandanten from "@/pages/Mandanten";
import Nutzer from "@/pages/Nutzer";
import AuditLog from "@/pages/AuditLog";
import Regeln from "@/pages/Regeln";
import Legal from "@/pages/Legal";
import About from "@/pages/About";

function Protected({ children, allowReview = false }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const { t } = useLanguage();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono text-sm">{t("lädt…")}</div>;
  if (!user) return <Navigate to="/login" replace />;
  const tenantPages = ["/", "/mandanten", "/media", "/analyse", "/performance", "/kommentare", "/fristen", "/regeln", "/audit"];
  if (user.review_access && !allowReview && !(user.review_access.tenant_view && tenantPages.includes(pathname))) return <Navigate to="/plan" replace />;
  return (
    <ClientProvider>
      <Layout>{children}</Layout>
    </ClientProvider>
  );
}

function GoogleCallback() {
  const { loginGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.replace("#", "")).get("session_id");
    if (sid) {
      loginGoogle(sid).then(() => navigate("/", { replace: true })).catch(() => navigate("/login", { replace: true }));
    } else {
      navigate("/login", { replace: true });
    }
  }, []);
  return <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono text-sm">{t("Google-Anmeldung…")}</div>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <GoogleCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/about" element={<About />} />
      <Route path="/about/mpai" element={<About tenant="mpai" />} />
      <Route path="/terms" element={<Legal type="terms" locale="en" />} />
      <Route path="/privacy" element={<Legal type="privacy" locale="en" />} />
      <Route path="/nutzungsbedingungen" element={<Legal type="terms" locale="de" />} />
      <Route path="/datenschutz" element={<Legal type="privacy" locale="de" />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/import" element={<Protected><ImportWizard /></Protected>} />
      <Route path="/plan" element={<Protected allowReview><Redaktionsplan /></Protected>} />
      <Route path="/media" element={<Protected><MediaLibrary /></Protected>} />
      <Route path="/analyse" element={<Protected><Analyse /></Protected>} />
      <Route path="/performance" element={<Protected><Performance /></Protected>} />
      <Route path="/kommentare" element={<Protected><Kommentare /></Protected>} />
      <Route path="/accounts" element={<Protected allowReview><Accounts /></Protected>} />
      <Route path="/fristen" element={<Protected><Fristen /></Protected>} />
      <Route path="/mandanten" element={<Protected><Mandanten /></Protected>} />
      <Route path="/nutzer" element={<Protected><Nutzer /></Protected>} />
      <Route path="/audit" element={<Protected><AuditLog /></Protected>} />
      <Route path="/regeln" element={<Protected><Regeln /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LanguageProvider><BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter></LanguageProvider>
      </AuthProvider>
    </div>
  );
}
